import { z } from 'zod';

/**
 * Password validation constants - must match backend SharedRules.BusinessRules
 */
export const PASSWORD_RULES = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 128,
  SPECIAL_CHARS: '@$!%*?&',
} as const;

/**
 * Password requirement definitions for UI display
 */
export const STANDARD_PASSWORD_REQUIREMENTS = [
  { id: 'length', label: `At least ${PASSWORD_RULES.MIN_LENGTH} characters`, test: (p: string) => p.length >= PASSWORD_RULES.MIN_LENGTH },
  { id: 'uppercase', label: 'At least one uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'At least one lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { id: 'digit', label: 'At least one number', test: (p: string) => /\d/.test(p) },
] as const;

export const STRONG_PASSWORD_REQUIREMENTS = [
  ...STANDARD_PASSWORD_REQUIREMENTS,
  { id: 'special', label: `At least one special character (${PASSWORD_RULES.SPECIAL_CHARS})`, test: (p: string) => new RegExp(`[${PASSWORD_RULES.SPECIAL_CHARS}]`).test(p) },
] as const;

/**
 * Standard password Zod schema - used for reset password
 * Requires: length, uppercase, lowercase, digit
 */
export const standardPasswordSchema = z.string()
  .min(1, 'Password is required')
  .min(PASSWORD_RULES.MIN_LENGTH, `Password must be at least ${PASSWORD_RULES.MIN_LENGTH} characters`)
  .max(PASSWORD_RULES.MAX_LENGTH, `Password must not exceed ${PASSWORD_RULES.MAX_LENGTH} characters`)
  .refine((password) => /[A-Z]/.test(password), {
    message: 'Password must contain at least one uppercase letter',
  })
  .refine((password) => /[a-z]/.test(password), {
    message: 'Password must contain at least one lowercase letter',
  })
  .refine((password) => /\d/.test(password), {
    message: 'Password must contain at least one number',
  });

/**
 * Strong password Zod schema - used for change password
 * Requires: length, uppercase, lowercase, digit, special character
 */
export const strongPasswordSchema = z.string()
  .min(1, 'Password is required')
  .min(PASSWORD_RULES.MIN_LENGTH, `Password must be at least ${PASSWORD_RULES.MIN_LENGTH} characters`)
  .max(PASSWORD_RULES.MAX_LENGTH, `Password must not exceed ${PASSWORD_RULES.MAX_LENGTH} characters`)
  .refine((password) => /[A-Z]/.test(password), {
    message: 'Password must contain at least one uppercase letter',
  })
  .refine((password) => /[a-z]/.test(password), {
    message: 'Password must contain at least one lowercase letter',
  })
  .refine((password) => /\d/.test(password), {
    message: 'Password must contain at least one number',
  })
  .refine((password) => new RegExp(`[${PASSWORD_RULES.SPECIAL_CHARS}]`).test(password), {
    message: `Password must contain at least one special character (${PASSWORD_RULES.SPECIAL_CHARS})`,
  });

/**
 * Reset password form schema
 */
export const resetPasswordFormSchema = z.object({
  newPassword: standardPasswordSchema,
  confirmPassword: z.string().min(1, 'Password confirmation is required'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

/**
 * Change password form schema
 */
export const changePasswordFormSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: strongPasswordSchema,
  confirmPassword: z.string().min(1, 'Password confirmation is required'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine((data) => data.newPassword !== data.currentPassword, {
  message: "New password must be different from current password",
  path: ["newPassword"],
});

export type ResetPasswordFormData = z.infer<typeof resetPasswordFormSchema>;
export type ChangePasswordFormData = z.infer<typeof changePasswordFormSchema>;

/**
 * Check which password requirements are met
 */
export function checkPasswordRequirements(
  password: string,
  requirements: typeof STANDARD_PASSWORD_REQUIREMENTS | typeof STRONG_PASSWORD_REQUIREMENTS
): { id: string; label: string; met: boolean }[] {
  return requirements.map(req => ({
    id: req.id,
    label: req.label,
    met: req.test(password),
  }));
}
